import React, { useState, useEffect, useMemo } from "react";
import { useUseCaseConfig } from "./useUseCaseConfig";
import * as S from "../shared/styles";
import { ROLE, ROLE_LABELS, RoleValue, PermissionsMember } from "../shared/types";
import { useAuditAccess } from "../shared/useAuditAccess";
import auditModuleService from "src/services/auditModuleService";

// ----------------------------  Role Rules  ----------------------------
//
// Members are synced from the team roster, which also controls removal; this page
// only sets each member's role and Authorized Use Cases.
//
// Admin         - root role. Not changed here; authorized on every use case.
// Program Owner - assigned by an Admin, who also grants their Authorized Use Cases.
// Auditor       - granted Authorized Use Cases by a Program Owner (or an Admin).
//
// The server returns, per member, the roles the caller may set (assignable_roles);
// an empty list means the caller cannot manage that member at all.

const isElevated = (m: PermissionsMember) => Boolean(m.elevated) || m.role === ROLE.ADMIN;

// The server returns no assignable roles for an Admin row or an out-of-scope member.
const canManageMember = (m: PermissionsMember) => (m.assignable_roles?.length ?? 0) > 0;

// ----------------------------  PermissionsPage  ----------------------------

const PermissionsPage: React.FC = () => {
  const { configs } = useUseCaseConfig();
  const { isAdmin, canManageUseCase } = useAuditAccess();
  const [members, setMembers] = useState<PermissionsMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Use cases the caller sees and grants: all for an Admin; for a Program Owner, only
  // the use cases an Admin assigned to them.
  const grantableUseCases = useMemo(
    () => configs.filter((u) => canManageUseCase(u.id)),
    [configs, canManageUseCase],
  );

  // A member's use cases as the caller sees them (an Admin row holds every one).
  const useCasesOf = (m: PermissionsMember) =>
    isElevated(m)
      ? grantableUseCases
      : grantableUseCases.filter((u) => m.assigned_use_case_ids.includes(u.id));

  // Match on alias, name, role label or authorized use case name (case-insensitive).
  const visibleMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      return [m.login, m.name, ROLE_LABELS[m.role]]
        .concat(useCasesOf(m).map((u) => u.name))
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [members, search, grantableUseCases]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    auditModuleService
      .getPermissionMembers()
      .then((data) => {
        if (!cancelled) setMembers(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 403) {
          setError(
            "You do not have permission to manage the permissions group. " +
              "An audit Admin or Program Owner role is required (HTTP 403).",
          );
        } else if (status) {
          setError("Failed to load members (HTTP " + status + (detail ? ": " + detail : "") + ").");
        } else {
          setError("Failed to load members. Please refresh to try again.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const replaceMember = (updated: PermissionsMember) =>
    setMembers((p) => p.map((m) => (m.login === updated.login ? updated : m)));

  const toggleAssignment = async (login: string, ucId: string) => {
    const member = members.find((m) => m.login === login);
    if (!member || !canManageMember(member) || !canManageUseCase(ucId)) return;
    const cur = member.assigned_use_case_ids;
    const next = cur.includes(ucId) ? cur.filter((x) => x !== ucId) : [...cur, ucId];
    const prev = members;
    setMembers((p) =>
      p.map((m) => (m.login === login ? { ...m, assigned_use_case_ids: next } : m)),
    );
    try {
      replaceMember(await auditModuleService.setMemberUseCases(login, next));
    } catch {
      setMembers(prev);
    }
  };

  const changeRole = async (login: string, role: RoleValue) => {
    const member = members.find((m) => m.login === login);
    if (!member || !member.assignable_roles?.includes(role)) return;
    const prev = members;
    setMembers((p) => p.map((m) => (m.login === login ? { ...m, role } : m)));
    try {
      replaceMember(await auditModuleService.updateMemberRole(login, role));
    } catch {
      setMembers(prev);
    }
  };

  return (
    <div style={S.pageWrapper}>
      {/* Header */}
      <div style={{ marginBottom: "20px" }}>
        <h1 style={S.pageTitle}>Permissions &amp; Access</h1>
        <p style={S.pageSubtitle}>
          {isAdmin
            ? "Set each member's role and grant their Authorized Use Cases. " +
              "Members are synced from the team; Admins are root accounts and can't be changed here."
            : "Grant Auditors access to the use cases assigned to you."}
        </p>
      </div>

      {/* Members table */}
      <div style={S.configCard}>
        <div style={cardHeader}>
          <span style={cardTitle}>
            Permissions Group (
            {search.trim() ? visibleMembers.length + " of " + members.length : members.length})
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members by alias, role or use case"
            aria-label="Search members"
            style={searchInput}
          />
        </div>

        {loading ? (
          <p style={statusText}>Loading members…</p>
        ) : error ? (
          <p style={{ ...statusText, color: "#ef4444" }}>{error}</p>
        ) : (
          <table style={{ width: "100%", fontSize: "0.875rem", borderCollapse: "collapse" }}>
            <thead>
              <tr style={theadRow}>
                <th style={th}>Member</th>
                <th style={th}>Role</th>
                <th style={th}>Authorized Use Cases</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ ...td, color: "#9ca3af" }}>
                    No members yet.
                  </td>
                </tr>
              ) : search.trim() && visibleMembers.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ ...td, color: "#9ca3af" }}>
                    No members match &quot;{search.trim()}&quot;.
                  </td>
                </tr>
              ) : null}
              {visibleMembers.map((m) => {
                const elevated = isElevated(m);
                const manageable = canManageMember(m);
                const roleOptions = m.assignable_roles ?? [];
                const shownUseCases = useCasesOf(m);
                return (
                  <tr key={m.login} style={tbodyRow}>
                    <td style={td}>
                      <div style={{ fontWeight: 500, color: "#1f2937" }}>{m.name}</div>
                      <div style={{ fontSize: "0.75rem", color: "#9ca3af" }}>{m.login}</div>
                    </td>
                    <td style={td}>
                      {elevated ? (
                        <span style={mutedText} title="Root role; can't be changed here">
                          Admin
                        </span>
                      ) : isAdmin && manageable && roleOptions.length > 1 ? (
                        <select
                          value={m.role}
                          onChange={(e) => changeRole(m.login, e.target.value as RoleValue)}
                          style={roleSelect}
                          aria-label={"Role for " + m.login}
                        >
                          {roleOptions.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span style={roleText}>{ROLE_LABELS[m.role]}</span>
                      )}
                    </td>
                    <td style={td}>
                      {elevated && isAdmin ? (
                        <span style={mutedText}>All use cases</span>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {manageable
                            ? grantableUseCases.map((u) => {
                                const on = m.assigned_use_case_ids.includes(u.id);
                                return (
                                  <button
                                    key={u.id}
                                    onClick={() => toggleAssignment(m.login, u.id)}
                                    style={on ? ucPillOn : ucPillOff}
                                    aria-pressed={on}
                                  >
                                    {u.name || u.id}
                                  </button>
                                );
                              })
                            : shownUseCases.map((u) => (
                                <span key={u.id} style={ucPillLocked}>
                                  {u.name || u.id}
                                </span>
                              ))}
                          {!elevated && shownUseCases.length === 0 && (
                            <span style={{ fontSize: "0.75rem", color: "#ef4444" }}>
                              {manageable ? "No access -- assign at least one" : "No access"}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "12px" }}>
          Auditors only see tasks for their authorized use cases and can't access Configuration.
          Changes take effect immediately and are logged to the audit trail.
        </p>
      </div>
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const cardHeader: React.CSSProperties = {
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
};

const searchInput: React.CSSProperties = {
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  padding: "6px 10px",
  fontSize: "0.875rem",
  width: "44ch",
  maxWidth: "100%",
};

const cardTitle: React.CSSProperties = {
  fontSize: "1rem",
  fontWeight: 600,
  color: "#111827",
};

const statusText: React.CSSProperties = {
  fontSize: "0.875rem",
  color: "#6b7280",
  padding: "16px 0",
};

const theadRow: React.CSSProperties = {
  textAlign: "left",
  fontSize: "0.75rem",
  color: "#9ca3af",
  borderBottom: "1px solid #e5e7eb",
};

const th: React.CSSProperties = {
  padding: "8px 12px 8px 0",
  fontWeight: 500,
};

const tbodyRow: React.CSSProperties = {
  borderBottom: "1px solid #f3f4f6",
  verticalAlign: "top",
};

const td: React.CSSProperties = {
  padding: "12px 12px 12px 0",
};

const roleSelect: React.CSSProperties = {
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  padding: "4px 8px",
  fontSize: "0.875rem",
  background: "white",
};

const ucPillBase: React.CSSProperties = {
  fontSize: "0.75rem",
  padding: "2px 8px",
  borderRadius: "9999px",
  border: "1px solid",
  fontWeight: 500,
  cursor: "pointer",
  background: "none",
};

const ucPillOn: React.CSSProperties = {
  ...ucPillBase,
  background: "#0f7b8a",
  color: "white",
  borderColor: "#0f7b8a",
};

const ucPillOff: React.CSSProperties = {
  ...ucPillBase,
  background: "none",
  color: "#6b7280",
  borderColor: "#d1d5db",
};

const ucPillLocked: React.CSSProperties = {
  ...ucPillBase,
  cursor: "default",
  background: "#f3f4f6",
  color: "#4b5563",
  borderColor: "#e5e7eb",
};

// Read-only role / use-case text (Admin rows): same size as the role text, grey.
const mutedText: React.CSSProperties = {
  fontSize: "0.875rem",
  color: "#6b7280",
};

const roleText: React.CSSProperties = {
  fontSize: "0.875rem",
  color: "#374151",
};

export default PermissionsPage;
